import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Trash2, MicOff, Mic, UserMinus, Ban, UserCheck,
  LogIn, LogOut, Inbox,
} from 'lucide-react';
import { api, timeAgo, formatDuration } from '../api.js';
import { useGuild } from './GuildLayout.jsx';
import { UserChip } from '../components/UserChip.jsx';

const ACTION_META = {
  warn:            { Icon: AlertTriangle, cls: 'warn',    label: 'warned' },
  warning_deleted: { Icon: Trash2,        cls: 'dim',     label: 'deleted warning' },
  timeout:         { Icon: MicOff,        cls: 'warn',    label: 'timed out' },
  untimeout:       { Icon: Mic,           cls: 'success', label: 'removed timeout from' },
  kick:            { Icon: UserMinus,     cls: 'danger',  label: 'kicked' },
  ban:             { Icon: Ban,           cls: 'danger',  label: 'banned' },
  unban:           { Icon: UserCheck,     cls: 'success', label: 'unbanned' },
};
const EVENT_META = {
  join:  { Icon: LogIn,  cls: 'success', label: 'joined' },
  leave: { Icon: LogOut, cls: 'danger',  label: 'left' },
};

export default function Overview() {
  const { guildId, guild } = useGuild();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    setStats(null);
    setActivity(null);
    api.guildStats(guildId).then(setStats);
    api.guildRecent(guildId, 25).then(r => setActivity(r.activity));
  }, [guildId]);

  return (
    <>
      <h1>Overview</h1>
      <p className="subtitle">A pulse on this server's recent activity.</p>

      <div className="stat-grid">
        <StatTile color="info"    label="Members"          value={guild?.member_count ?? '—'}
                  delta={guild?.online_count != null ? <><strong>{guild.online_count}</strong> online now</> : null} />
        <StatTile color="success" label="Joins (7d)"       value={stats?.joins_7d}
                  delta={stats && <><strong>{stats.leaves_7d}</strong> left</>} />
        <StatTile color="warn"    label="Warnings"         value={stats?.warnings_total}
                  delta={stats && <><strong>{stats.warnings_7d}</strong> in last 7d</>} />
        <StatTile color="danger"  label="Mod actions (7d)" value={stats?.mod_actions_7d}
                  delta={stats && <><strong>{stats.mod_actions_24h}</strong> in last 24h</>} />
      </div>

      <h3>Recent activity</h3>
      {activity === null ? (
        <SkeletonList rows={8} />
      ) : !activity.length ? (
        <div className="card empty">
          <div className="ico"><Inbox size={32} strokeWidth={1.5} /></div>
          <p>No activity yet. As people join and moderation happens, it'll show up here.</p>
        </div>
      ) : (
        <div className="card activity">
          {activity.map(a => <ActivityRow key={`${a.kind}-${a.id}`} item={a} guildId={guildId} />)}
        </div>
      )}
    </>
  );
}

function StatTile({ color, label, value, delta }) {
  return (
    <div className={`stat ${color}`}>
      <div className="label">{label}</div>
      <div className="value">{value === undefined || value === null ? <span className="skel" style={{ display: 'inline-block', width: 60, height: 28 }} /> : value}</div>
      {delta && <div className="delta">{delta}</div>}
    </div>
  );
}

function ActivityRow({ item, guildId }) {
  if (item.kind === 'member') {
    const meta = EVENT_META[item.detail];
    const Icon = meta?.Icon;
    return (
      <div className="row-item">
        <div className={`ico ${meta?.cls || 'dim'}`}>{Icon ? <Icon size={16} strokeWidth={2} /> : '·'}</div>
        <div className="text">
          <div className="head">
            <strong>{item.reason || 'Unknown'}</strong> {meta?.label || item.detail}
          </div>
          <div className="sub">
            <Link to={`/g/${guildId}/users/${item.user_id}`} className="tag">{item.user_id}</Link>
          </div>
        </div>
        <div className="when">{timeAgo(item.created_at)}</div>
      </div>
    );
  }
  const meta = ACTION_META[item.detail];
  const Icon = meta?.Icon;
  const knownMod = item.moderator_id && item.moderator_id !== 'unknown';
  return (
    <div className="row-item">
      <div className={`ico ${meta?.cls || 'dim'}`}>{Icon ? <Icon size={16} strokeWidth={2} /> : '·'}</div>
      <div className="text">
        <div className="head" style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          {knownMod
            ? <UserChip guildId={guildId} userId={item.moderator_id} size={18} />
            : <span className="dim">someone</span>}
          <span>{meta?.label || item.detail}</span>
          <UserChip guildId={guildId} userId={item.user_id} size={18} />
          {item.duration_ms && <span className="dim"> · {formatDuration(item.duration_ms)}</span>}
        </div>
        {item.reason && <div className="sub">{item.reason}</div>}
      </div>
      <div className="when">{timeAgo(item.created_at)}</div>
    </div>
  );
}

function SkeletonList({ rows }) {
  return (
    <div className="card activity">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="row-item">
          <div className="skel" style={{ width: 32, height: 32, borderRadius: '50%' }} />
          <div className="text" style={{ flex: 1 }}>
            <div className="skel skel-line med" />
            <div className="skel skel-line short" />
          </div>
        </div>
      ))}
    </div>
  );
}
